/** Hospitality domain — mesas y comandas (restaurant_full / quick_service_food). */

export type DiningTableStatus = 'available' | 'occupied' | 'reserved' | 'closed';

export type DiningTable = {
  id: string;
  branchId: string;
  name: string;
  capacity: number;
  status: DiningTableStatus;
  /** Orden relativo de la mesa en el salón. */
  sortOrder: number;
  /** Timestamp cuando la mesa pasó a occupied. Null si no está occupied. */
  occupiedAt?: string | null;
  /** User asignado como mozo. Null = sin asignar. */
  assignedUserId?: string | null;
  /** Posición X en el mapa del salón (0-1 relativo). Null = auto-layout. */
  posX?: number | null;
  /** Posición Y en el mapa del salón (0-1 relativo). Null = auto-layout. */
  posY?: number | null;
  /** Última actualización del registro. */
  updatedAt?: string | null;
  /** Token QR tableside `/m/{slug}?t=…`. */
  publicToken?: string | null;
};

/** Labels unificados — Mesas + picker POS. closed ≠ available. */
export const DINING_TABLE_STATUS_LABELS: Record<DiningTableStatus, string> = {
  available: 'Libre',
  occupied: 'Ocupada',
  reserved: 'Reservada',
  closed: 'Cerrada',
};

/** Claves de color para cada estado. El componente las resuelve contra el tema. */
export const DINING_TABLE_STATUS_COLORS: Record<DiningTableStatus, 'success' | 'danger' | 'warning' | 'muted'> = {
  available: 'success',
  occupied: 'danger',
  reserved: 'warning',
  closed: 'muted',
};

export type KitchenTicketStatus = 'pending' | 'preparing' | 'ready' | 'served' | 'cancelled';

export type KitchenTicketLine = {
  name: string;
  qty: number;
  optionsLabel?: string | null;
  lineNote?: string | null;
  productId?: string | null;
  /** Opciones estructuradas para reconstruir el carrito POS al cobrar. */
  menuOptions?: Array<{
    groupId: string;
    groupName: string;
    choiceId: string;
    choiceName: string;
    priceDelta: number;
    qty: number;
    linkedProductId?: string | null;
    modifierKind?: 'extra' | 'exclude' | 'detail' | null;
    allergenTags?: string[];
    kitchenLabel?: string | null;
    substitutesChoiceId?: string | null;
    substitutesProductId?: string | null;
    slotProductId?: string | null;
  }> | null;
  /** Estado de preparación de esta línea cuando el KDS soporta bump por línea (KDS-S10). */
  status?: 'pending' | 'done' | null;
  /** Nombre del combo padre cuando la línea es un componente expandido. */
  comboName?: string | null;
};

export type KitchenTicket = {
  id: string;
  branchId: string;
  saleId?: string | null;
  diningTableId?: string | null;
  /** Online order that created this ticket; null for POS-only. */
  customerOrderId?: string | null;
  status: KitchenTicketStatus;
  /** When true, ticket is on hold (fire later). KDS blocks bump. */
  hold?: boolean;
  /** When true, this is an all-day item (drinks, desserts). */
  isAllDay?: boolean;
  /** When true, this ticket is marked as urgent/rush. */
  rush?: boolean;
  /** Fulfillment type for online orders: pickup, delivery, dine_in. Null for POS. */
  fulfillmentType?: string | null;
  notes?: string | null;
  lines?: KitchenTicketLine[];
  createdAt?: string | null;
  /** When the ticket entered its current status (server). */
  statusEnteredAt?: string | null;
  /** Linked online order — shown when Kitchen.view_customer. */
  customerName?: string | null;
  customerPhone?: string | null;
  /** Server-derived preparation SLA deadline; null for legacy/unfired tickets. */
  slaDeadlineAt?: string | null;
  /** 'pending' = comanda disparada antes de cobrar (fire-before-pay); served queda bloqueado hasta attach a la venta. */
  paymentState?: 'not_required' | 'pending' | 'paid' | null;
};

export type Station = {
  id: string;
  branchId: string;
  name: string;
  sortOrder: number;
};

export type ProductStation = {
  productId: string;
  stationId: string;
};

export type ReservationStatus = 'pending' | 'confirmed' | 'seated' | 'cancelled' | 'no_show';

export type Reservation = {
  id: string;
  branchId: string;
  diningTableId?: string | null;
  customerName: string;
  customerPhone?: string | null;
  partySize: number;
  reservationDate: string;
  reservationTime: string;
  durationMinutes: number;
  status: ReservationStatus;
  notes?: string | null;
  /** `portal` = tienda online; `app` = cargada en el negocio. */
  source?: 'portal' | 'app';
  createdAt?: string | null;
};

/** Cuenta abierta por mesa (VEN-S2 / POS-S3). */
export type TableSession = {
  id: string;
  diningTableId: string;
  branchId: string;
  covers: number;
  openedAt?: string | null;
  cartJson?: unknown[];
  promoJson?: unknown | null;
  lineCount?: number;
  created?: boolean;
  /** Tip sugerido guest/staff antes del cobro. */
  tipIntent?: {
    amount: number;
    percent: number | null;
    source: 'guest' | 'staff';
  } | null;
  /** Mozo asignado de la mesa (para primary_staff). */
  assignedUserId?: string | null;
  /** unpaid | pending | partial | paid — guest MP en curso. */
  paymentStatus?: string | null;
};

/** Salud de la cola durable de impresión (get_hospitality_snapshot.print_queue). */
export type KitchenPrintQueueHealth = {
  /** Jobs sin imprimir: queued + retrying (+printing si un agente lo marca). */
  pendingCount: number;
  /** Jobs que agotaron intentos y esperan reintento manual. */
  failedCount: number;
  /** Pendientes más viejas que stuckAfterMinutes → agente/impresora offline. */
  stuckCount: number;
  oldestQueuedAt: string | null;
  stuckAfterMinutes: number;
};

/** Resumen por mesa desde get_hospitality_snapshot.table_drafts (ADR-073). */
export type TableDraftSummary = {
  diningTableId: string;
  branchId: string;
  draftId: string | null;
  draftLineCount: number;
  draftUpdatedAt?: string | null;
  openSignalCount: number;
  hasReadyToOrder: boolean;
  hasCallWaiter: boolean;
  hasRequestBill: boolean;
  lookingGuestCount: number;
};

export type TableOrderDraftLine = {
  id: string;
  clientLineId: string;
  productId: string | null;
  name: string;
  qty: number;
  unitPrice: number;
  unitNote: string | null;
  optionsLabel: string | null;
  optionsJson: unknown[];
  addedByRole: 'guest' | 'staff' | string;
  addedByDeviceId: string | null;
};

export type TableOrderDraft = {
  id: string;
  diningTableId: string;
  tableSessionId: string | null;
  status: string;
  version: number;
  updatedAt: string;
  lastActivityAt: string;
  lines: TableOrderDraftLine[];
};

export type GuestSignal = {
  id: string;
  kind: 'call_waiter' | 'ready_to_order' | 'request_bill' | string;
  status: string;
  createdAt: string;
  guestDeviceId?: string | null;
};

export type StaffVisitSaleLine = {
  name: string;
  qty: number;
  price: number;
  lineNote?: string | null;
  optionsLabel?: string | null;
};

/** Cobro cerrado de la visita actual (desde occupied_at). */
export type StaffVisitSale = {
  sessionId: string;
  saleId: string;
  closedAt: string | null;
  total: number;
  lineCount: number;
  lines: StaffVisitSaleLine[];
};

export type StaffTableAccount = {
  tableStatus: DiningTableStatus | string;
  needsLiberate: boolean;
  occupiedAt: string | null;
  assignedUserId: string | null;
  sessionId: string | null;
  sessionStatus: 'open' | 'closed' | string | null;
  openedAt: string | null;
  closedAt: string | null;
  saleId: string | null;
  covers: number;
  cartJson: unknown[];
  /** Ítems (suma qty) en tickets pending|preparing|ready sin sale_id. */
  openKitchenCount: number;
  /** Ítems en ready sin sale_id (listos para servir). */
  readyKitchenCount: number;
  /** Líneas ready sin sale_id (nombre + qty) para la ficha. */
  kitchenReadyLines: Array<{ name: string; qty: number }>;
  /** Tip intent de la sesión abierta (guest/staff). */
  tipIntent?: {
    amount: number;
    percent: number | null;
    source: 'guest' | 'staff';
  } | null;
};

export type StaffTableDraftPayload = {
  draft: TableOrderDraft | null;
  openSignals: GuestSignal[];
  presence: Array<{ guestDeviceId: string; lastSeenAt: string }>;
  account: StaffTableAccount | null;
  /** Todos los cobros de la visita (más reciente primero). */
  visitSales: StaffVisitSale[];
};
